import { useState, useEffect } from "react";
import { X, Calendar, Clock, Edit, Save, Loader2, List } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import Swal from "sweetalert2";
import { 
  obtenerHorariosZonaComun,
  modificarHorarioZonaComun,
  obtenerTodosDiasUtilizacion,
  HorarioZonaComun,
  DiaUtilizacion
} from "../../../../api/zonaComunHorarioService";
import TurnosPorDiaModal from "./TurnosPorDiaModal";

interface DetailDiaHabilModalProps {
  isOpen: boolean;
  horarioCodigo: number;
  zonaComunCodigo: string;
  onClose: (shouldReload: boolean) => void;
}

export default function DetailDiaHabilModal({ 
  isOpen, 
  horarioCodigo,
  zonaComunCodigo,
  onClose 
}: DetailDiaHabilModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [horario, setHorario] = useState<HorarioZonaComun | null>(null);
  const [nombreDia, setNombreDia] = useState("");
  const [horaApertura, setHoraApertura] = useState("");
  const [horaCierre, setHoraCierre] = useState("");
  const [diasUtilizacion, setDiasUtilizacion] = useState<DiaUtilizacion[]>([]);
  const [showTurnosModal, setShowTurnosModal] = useState(false);

  useEffect(() => {
    if (isOpen && horarioCodigo && zonaComunCodigo) {
      setIsEditing(false);
      loadDiasUtilizacion().then(() => {
        loadHorario();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, horarioCodigo, zonaComunCodigo]);

  useEffect(() => {
    // Actualizar nombre del día cuando se carga el horario y los días están disponibles
    if (horario && diasUtilizacion.length > 0) {
      const dia = diasUtilizacion.find(d => d.ddu_codigo === horario.zhd_dia_de_utilizacion);
      setNombreDia(dia ? dia.ddu_nombre : horario.zhd_dia_de_utilizacion);
    }
  }, [horario, diasUtilizacion]);

  const loadDiasUtilizacion = async () => {
    try {
      const response = await obtenerTodosDiasUtilizacion();
      if (response.code === "1" && response.object_) {
        setDiasUtilizacion(response.object_);
      }
    } catch (error) {
      console.error("Error al cargar días de utilización:", error);
    }
  };

  const loadHorario = async () => {
    if (!horarioCodigo || !zonaComunCodigo) return;

    try {
      setIsLoading(true);
      const payload = {
        codigo: horarioCodigo.toString(),
        "zona comun codigo": zonaComunCodigo,
        "dia de utilizacion codigo": "",
      };

      const response = await obtenerHorariosZonaComun(payload);

      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        const horarioData = response.object_[0];
        setHorario(horarioData);
        setHoraApertura(horarioData.zhd_hora_apertura);
        setHoraCierre(horarioData.zhd_hora_cierre);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar la información del día hábil",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
      }
    } catch (error) {
      console.error("Error al cargar horario:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar la información del día hábil",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateTimeFormat = (time: string): boolean => {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return timeRegex.test(time);
  };

  const handleSave = async () => {
    if (!horaApertura) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor especifique la hora de apertura",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!validateTimeFormat(horaApertura)) {
      Swal.fire({
        icon: "warning",
        title: "Formato inválido",
        text: "La hora de apertura debe estar en formato militar (HH:MM). Ejemplo: 09:00",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!horaCierre) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor especifique la hora de cierre",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!validateTimeFormat(horaCierre)) {
      Swal.fire({
        icon: "warning",
        title: "Formato inválido",
        text: "La hora de cierre debe estar en formato militar (HH:MM). Ejemplo: 19:00",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    // Validar que la hora de cierre sea posterior a la hora de apertura
    const [aperturaHora, aperturaMinuto] = horaApertura.split(':').map(Number);
    const [cierreHora, cierreMinuto] = horaCierre.split(':').map(Number);
    const aperturaTotal = aperturaHora * 60 + aperturaMinuto;
    const cierreTotal = cierreHora * 60 + cierreMinuto;

    if (cierreTotal <= aperturaTotal) {
      Swal.fire({
        icon: "warning",
        title: "Horario inválido",
        text: "La hora de cierre debe ser posterior a la hora de apertura",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        codigo: horarioCodigo,
        "zona comun codigo": zonaComunCodigo,
        "hora apertura": horaApertura,
        "hora cierre": horaCierre,
      };

      const response = await modificarHorarioZonaComun(payload);

      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "¡Horario actualizado!",
          text: response.message || "El horario se ha actualizado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
        setIsEditing(false);
        loadHorario(); // Recargar datos actualizados
        onClose(true);
      } else {
        throw new Error(response.message || "Error al actualizar el horario");
      }
    } catch (error) {
      console.error("Error al actualizar horario:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al actualizar el horario";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
          onClose(false);
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Calendar size={28} />
            {isEditing ? "Editar Día Hábil" : "Detalle Día Hábil"}
          </h2>
          <button
            onClick={() => onClose(false)}
            className="text-white hover:bg-white/20 rounded-full p-1 transition"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-military-600 mb-4" />
              <p className="text-gray-600">Cargando información...</p>
            </div>
          ) : !horario ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-gray-600">No se pudo cargar la información</p>
            </div>
          ) : (
            <>
              {/* Banner informativo sutil */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="text-gray-500" size={18} />
                    <span className="text-sm font-semibold text-gray-700">{nombreDia}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-gray-500" />
                      <span className="text-gray-600">Apertura:</span>
                      <span className="font-medium text-gray-800">{horaApertura}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-gray-500" />
                      <span className="text-gray-600">Cierre:</span>
                      <span className="font-medium text-gray-800">{horaCierre}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Calendar size={16} className="inline mr-2 text-military-600" />
                  Día de la Semana
                </label>
                <p className="text-gray-800 p-3 bg-gray-50 rounded-lg font-semibold">
                  {nombreDia}
                </p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Clock size={16} className="inline mr-2 text-military-600" />
                  Hora de Apertura (Formato Militar) *
                </label>
                {isEditing ? (
                  <>
                    <Input
                      type="text"
                      value={horaApertura}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^([01]?\d|2[0-3]):?([0-5]?\d?)$/.test(value)) {
                          if (value.length === 2 && !value.includes(':')) {
                            setHoraApertura(value + ':');
                          } else {
                            setHoraApertura(value);
                          }
                        }
                      }}
                      placeholder="09:00"
                      disabled={isSubmitting}
                      className="w-full p-3 shadow-sm"
                      maxLength={5}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Formato: HH:MM (24 horas). Ejemplo: 09:00, 14:30
                    </p>
                  </>
                ) : (
                  <p className="text-gray-800 p-3 bg-gray-50 rounded-lg font-semibold">
                    {horaApertura}
                  </p>
                )}
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Clock size={16} className="inline mr-2 text-military-600" />
                  Hora de Cierre (Formato Militar) *
                </label>
                {isEditing ? (
                  <>
                    <Input
                      type="text"
                      value={horaCierre}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^([01]?\d|2[0-3]):?([0-5]?\d?)$/.test(value)) {
                          if (value.length === 2 && !value.includes(':')) {
                            setHoraCierre(value + ':');
                          } else {
                            setHoraCierre(value);
                          }
                        }
                      }}
                      placeholder="19:00"
                      disabled={isSubmitting}
                      className="w-full p-3 shadow-sm"
                      maxLength={5}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Formato: HH:MM (24 horas). Ejemplo: 19:00, 22:30
                    </p>
                  </>
                ) : (
                  <p className="text-gray-800 p-3 bg-gray-50 rounded-lg font-semibold">
                    {horaCierre}
                  </p>
                )}
              </div>

              {!isEditing && horario && (
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200 p-5 shadow-sm">
                  <Button
                    type="button"
                    onClick={() => setShowTurnosModal(true)}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white shadow-lg font-bold flex items-center justify-center gap-2 py-3"
                  >
                    <List size={20} />
                    Ver Turnos por Día
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t pt-4 pb-6 px-6">
          <div className="flex gap-4 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (isEditing) {
                  setIsEditing(false);
                  if (horario) {
                    setHoraApertura(horario.zhd_hora_apertura);
                    setHoraCierre(horario.zhd_hora_cierre);
                  }
                } else {
                  onClose(false);
                }
              }}
              disabled={isSubmitting}
              className="flex-1 border-2 hover:bg-gray-50"
            >
              {isEditing ? "Cancelar" : "Cerrar"}
            </Button>
            {!isEditing ? (
              <Button
                type="button"
                onClick={() => setIsEditing(true)}
                className="flex-1 bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center justify-center gap-2"
              >
                <Edit size={20} />
                Editar
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className="flex-1 bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Guardar Cambios
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {horario && (
        <TurnosPorDiaModal
          isOpen={showTurnosModal}
          horarioPorDiaCodigo={horario.zhd_codigo}
          zonaComunCodigo={zonaComunCodigo}
          nombreDia={nombreDia}
          horaApertura={horario.zhd_hora_apertura}
          horaCierre={horario.zhd_hora_cierre}
          onClose={() => setShowTurnosModal(false)}
        />
      )}
    </div>
  );
}

